import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import '../styles/flow-animations.css';

import { FlowNode, FlowEdge, FlowTemplate, FlowNodeType } from '../types/flowBuilder';
import FlowSidebar from '../components/flowBuilder/FlowSidebar';
import FlowCanvas from '../components/flowBuilder/FlowCanvas';
import NodePropertiesPanel from '../components/flowBuilder/NodePropertiesPanel';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { FLOW_ENGINE_ENDPOINTS } from '../config/flowEngine';
import { 
  CogIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  XMarkIcon,
  Bars3Icon,
  EyeIcon,
  KeyIcon,
  TrashIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';
import { 
  saveFlow, 
  updateFlow, 
  getFlow, 
  listFlows, 
  executeFlow, 
  validateFlow,
  exportFlow,
  importFlow,
  deleteFlow
} from '../services/flowBuilderService';
import FlowExecutionHistory from '../components/flowBuilder/FlowExecutionHistory';
import ExecutionLogs from '../components/flowBuilder/ExecutionLogs';
import FlowBuilderDocumentation from '../components/flowBuilder/FlowBuilderDocumentation';
import NotificationModal from '../components/common/NotificationModal';
import Modal from '../components/common/Modal';
import CredentialsManager from '../components/settings/CredentialsManager';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import FlowTemplates from '../components/flowBuilder/FlowTemplates';
import ExecutionMonitor from '../components/flowBuilder/ExecutionMonitor';

// Kezdeti node-ok példa adatokkal
const initialNodes: FlowNode[] = [
  {
    id: '1',
    type: 'trigger',
    position: { x: 250, y: 50 },
    data: {
      label: 'Webhook Trigger',
      description: 'Új ügyfél igény beérkezése',
      config: {
        webhookUrl: '/api/webhooks/customer-request',
        method: 'POST'
      },
      inputs: [],
      outputs: [
        { name: 'customer_data', type: 'object', description: 'Ügyfél adatok' }
      ]
    }
  }
];

const initialEdges: FlowEdge[] = [];

const FlowBuilder: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [executionProgress, setExecutionProgress] = useState<Record<string, number>>({});
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const [currentFlow, setCurrentFlow] = useState<FlowTemplate | null>(null);
  const [availableFlows, setAvailableFlows] = useState<FlowTemplate[]>([]);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } | null>(null);
  const [executionHistory, setExecutionHistory] = useState<any[]>([]);
  const [showExecutionHistory, setShowExecutionHistory] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showExecutionMonitor, setShowExecutionMonitor] = useState(false);
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>({ isOpen: false, type: 'info', title: '', message: '' });
  const [isEditingFlowName, setIsEditingFlowName] = useState(false);
  const [editingFlowName, setEditingFlowName] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState<{
    is_running: boolean;
    scheduled_flows_count: number;
    message: string;
  } | null>(null);
  const [engineStatus, setEngineStatus] = useState<{
    status: string;
    service: string;
    version: string;
    isOnline: boolean;
  } | null>(null);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showScheduledFlows, setShowScheduledFlows] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [flowSearchTerm, setFlowSearchTerm] = useState('');
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showFlowDropdown, setShowFlowDropdown] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);

  // Sidebar toggle funkció
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Node kapcsolás kezelése
  const onConnect = useCallback(
    (params: Connection) => {
      const edge: FlowEdge = {
        id: `${params.source}-${params.target}`,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: 'default',
        animated: false,
      };
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges]
  );

  // Node kiválasztás kezelése
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      setSelectedNode(node.id);
    },
    []
  );

  // Canvas kattintás kezelése (kijelölés törlése)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Node hozzáadása a sidebar-ból
  const onAddNode = useCallback(
    (nodeType: FlowNodeType, position: { x: number; y: number }) => {
      const newNode = {
        id: `${nodeType}-${Date.now()}`,
        type: nodeType,
        position,
        data: {
          label: getNodeLabel(nodeType),
          description: '',
          config: {},
          inputs: getDefaultInputs(nodeType),
          outputs: getDefaultOutputs(nodeType),
        }
      };
      
      setNodes((nds) => nds.concat(newNode));
      
      // Mobilon automatikusan bezárjuk a sidebar-t node hozzáadása után
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      }
    },
    [setNodes]
  );

  // Node tulajdonságok frissítése
  const onUpdateNode = useCallback(
    (nodeId: string, updates: any) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...updates } }
            : node
        )
      );
    },
    [setNodes]
  );

  // Node törlése
  const onDeleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setSelectedNode(null);
  }, [setNodes, setEdges]);

  // Flow Engine státusz lekérése
  const fetchEngineStatus = async () => {
    try {
      const response = await fetch(FLOW_ENGINE_ENDPOINTS.health);
      if (response.ok) {
        const status = await response.json();
        setEngineStatus({
          ...status,
          isOnline: true
        });
      } else {
        setEngineStatus({
          status: 'error',
          service: 'youcrm-flow-engine',
          version: 'unknown',
          isOnline: false
        });
      }
    } catch (error) {
      console.error('Failed to fetch engine status:', error);
      setEngineStatus({
        status: 'offline',
        service: 'youcrm-flow-engine',
        version: 'unknown',
        isOnline: false
      });
    }
  };

  // Scheduler státusz lekérése
  const fetchSchedulerStatus = async () => {
    try {
      const response = await fetch(`${FLOW_ENGINE_ENDPOINTS.scheduler}/status`);
      if (response.ok) {
        const status = await response.json();
        setSchedulerStatus(status);
      } else {
        setSchedulerStatus({
          is_running: false,
          scheduled_flows_count: 0,
          message: 'Scheduler not available'
        });
      }
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
      setSchedulerStatus({
        is_running: false,
        scheduled_flows_count: 0,
        message: 'Scheduler offline'
      });
    }
  };

  // Load available flows on component mount
  useEffect(() => {
    const loadFlows = async () => {
      if (!user?.organizationId) return;
      
      try {
        setIsLoading(true);
        const flows = await listFlows(user.organizationId);
        setAvailableFlows(flows);
      } catch (error) {
        console.error('Error loading flows:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFlows();
    fetchEngineStatus();
    fetchSchedulerStatus();
    
    // Státuszok frissítése 30 másodpercenként
    const interval = setInterval(() => {
      fetchEngineStatus();
      fetchSchedulerStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [user?.organizationId]);

  // Validate flow whenever nodes or edges change
  useEffect(() => {
    const result = validateFlow(nodes as FlowNode[], edges as FlowEdge[]);
    setValidationResult(result);
  }, [nodes, edges]);

  // Flow végrehajtás animációval
  const simulateExecutionProgress = useCallback(async (nodeIds: string[]) => {
    setExecutionProgress({});
    
    // Szimuláljuk a node-ok végrehajtását sorban
    for (let i = 0; i < nodeIds.length; i++) {
      const nodeId = nodeIds[i];
      
      // Node végrehajtás animáció (0-1 között)
      for (let progress = 0; progress <= 100; progress += 10) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms késleltetés
        setExecutionProgress(prev => ({
          ...prev,
          [nodeId]: progress / 100
        }));
      }
      
      // Kis szünet a következő node előtt
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }, []);

  // Flow végrehajtás
  const onExecuteFlow = useCallback(async (withMonitor: boolean = false) => {
    if (!currentFlow?.id || !user?.organizationId || !user?.uid) {
      console.error('Missing flow ID, organization ID, or user ID');
      return;
    }

    setIsExecuting(true);
    
    // Node-ok sorrendjének meghatározása (egyszerű topológiai rendezés)
    const nodeIds = nodes.map(node => node.id);
    
    try {
      console.log('Executing flow:', currentFlow.id);
      
      // Animáció indítása
      const animationPromise = simulateExecutionProgress(nodeIds);
      
      // Tényleges végrehajtás
      const result = await executeFlow({
        flowId: currentFlow.id,
        organizationId: user.organizationId,
        userId: user.uid,
        initialData: {
          executedAt: new Date().toISOString(),
          executedBy: user.uid
        }
      });
      
      // Várjuk meg az animáció befejezését
      await animationPromise;
      
      console.log('Flow execution result:', result);
      
      if (result.success && result.executionId) {
        // Execution Monitor megnyitása csak ha kérték
        if (withMonitor) {
          setCurrentExecutionId(result.executionId);
          setShowExecutionMonitor(true);
        }
        
        setNotification({
          isOpen: true,
          type: 'success',
          title: 'Sikeres végrehajtás',
          message: withMonitor 
            ? `A flow sikeresen elindítva! Végrehajtás ID: ${result.executionId}` 
            : `A flow sikeresen végrehajtva lett! Végrehajtás ID: ${result.executionId}`
        });
      } else {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Végrehajtási hiba',
          message: `A flow végrehajtása sikertelen: ${result.message}`
        });
      }
    } catch (error) {
      console.error('Flow execution error:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Végrehajtási hiba',
        message: `Hiba történt a flow végrehajtása során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
      });
    } finally {
      setIsExecuting(false);
      // Progress reset 2 másodperc után
      setTimeout(() => {
        setExecutionProgress({});
      }, 2000);
    }
  }, [currentFlow, user, nodes, edges, simulateExecutionProgress]);

  // Flow mentése
  const onSaveFlow = useCallback(async () => {
    if (!user?.organizationId || !user?.uid) {
      console.error('Missing organization ID or user ID');
      return;
    }

    setIsSaving(true);
    try {
      const flowData: Partial<FlowTemplate> = {
        name: currentFlow?.name || 'Új Flow',
        description: currentFlow?.description || 'Flow leírása',
        category: currentFlow?.category || 'custom',
        nodes: nodes as FlowNode[],
        edges: edges as FlowEdge[],
        variables: currentFlow?.variables || [],
        triggers: currentFlow?.triggers || [],
        isActive: true,
      };
      
      let result: { success: boolean; flowId?: string; message: string };
      if (currentFlow?.id) {
        // Update existing flow
        result = await updateFlow(currentFlow.id, flowData, user.organizationId, user.uid);
        console.log('Flow updated:', result);
      } else {
        // Create new flow
        result = await saveFlow(flowData, user.organizationId, user.uid);
        console.log('Flow saved:', result);
        
        // Set current flow ID for future updates
        if (result.flowId) {
          setCurrentFlow(prev => prev ? { ...prev, id: result.flowId! } : null);
        }
      }
      
      // Refresh flows list
      const updatedFlows = await listFlows(user.organizationId);
      setAvailableFlows(updatedFlows);
      
      setNotification({
        isOpen: true,
        type: 'success',
        title: 'Sikeres mentés',
        message: 'A flow sikeresen mentve lett!'
      });
    } catch (error) {
      console.error('Error saving flow:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Mentési hiba',
        message: `Hiba történt a mentés során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
      });
    } finally {
      setIsSaving(false);
    }
  }, [nodes, edges, currentFlow, user]);

  // Flow betöltése ID alapján
  const onLoadFlow = useCallback(async (flowId: string) => {
    if (!user?.organizationId) return;
    
    try {
      setIsLoading(true);
      const flow = await getFlow(flowId, user.organizationId);
      
      setCurrentFlow(flow);
      setNodes(flow.nodes || []);
      setEdges(flow.edges || []);
      
      // Auto-fit view after loading
      setTimeout(() => {
        if (reactFlowInstance && flow.nodes && flow.nodes.length > 0) {
          reactFlowInstance.fitView({ 
            padding: 0.2,
            duration: 400,
            maxZoom: 1.2
          });
        }
      }, 100);
      
      console.log('Flow loaded:', flow);
    } catch (error) {
      console.error('Error loading flow:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Betöltési hiba',
        message: `Hiba történt a flow betöltése során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
      });
    } finally {
      setIsLoading(false);
    }
  }, [user?.organizationId, setNodes, setEdges, reactFlowInstance]);

  // Új flow létrehozása
  const onNewFlow = useCallback(() => {
    setCurrentFlow({
      id: '',
      name: 'Új Flow',
      description: '',
      category: 'custom',
      nodes: [],
      edges: [],
      variables: [],
      triggers: [],
      isActive: true,
      organizationId: user?.organizationId || '',
      createdBy: user?.uid || '',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
  }, [user, setNodes, setEdges]);

  // Flow exportálása
  const onExportFlow = useCallback(() => {
    if (!currentFlow) return;
    
    const flowWithCurrentData = {
      ...currentFlow,
      nodes: nodes as FlowNode[],
      edges: edges as FlowEdge[]
    };
    
    const exportData = exportFlow(flowWithCurrentData);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentFlow.name || 'flow'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentFlow, nodes, edges]);

  // Flow importálása
  const onImportFlow = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = e.target?.result as string;
        const importedFlow = importFlow(jsonData);
        
        setCurrentFlow({
          id: '',
          ...importedFlow,
          organizationId: user?.organizationId || '',
          createdBy: user?.uid || '',
          createdAt: new Date(),
          updatedAt: new Date()
        } as FlowTemplate);
        
        setNodes(importedFlow.nodes || []);
        setEdges(importedFlow.edges || []);
        
        console.log('Flow imported:', importedFlow);
      } catch (error) {
        console.error('Error importing flow:', error);
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Importálási hiba',
          message: `Hiba történt a flow importálása során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
        });
      }
    };
    reader.readAsText(file);
    
    // Reset input
    event.target.value = '';
  }, [user, setNodes, setEdges]);

  // Flow átnevezés indítása
  const startEditingFlowName = useCallback(() => {
    if (currentFlow) {
      setEditingFlowName(currentFlow.name);
      setIsEditingFlowName(true);
    }
  }, [currentFlow]);

  // Flow átnevezés mentése
  const saveFlowName = useCallback(async () => {
    if (!currentFlow || !user?.organizationId || !user?.uid || !editingFlowName.trim()) {
      return;
    }

    try {
      setIsSaving(true);
      
      const updatedFlow = {
        ...currentFlow,
        name: editingFlowName.trim()
      };

      if (currentFlow.id) {
        // Meglévő flow átnevezése
        await updateFlow(currentFlow.id, { name: editingFlowName.trim() }, user.organizationId, user.uid);
      } else {
        // Új flow esetén csak a helyi state-et frissítjük
        // A tényleges mentés a "Mentés" gombbal történik majd
      }
      
      setCurrentFlow(updatedFlow);
      
      // Refresh flows list ha meglévő flow-t neveztünk át
      if (currentFlow.id) {
        const updatedFlows = await listFlows(user.organizationId);
        setAvailableFlows(updatedFlows);
      }
      
      setIsEditingFlowName(false);
      
      setNotification({
        isOpen: true,
        type: 'success',
        title: currentFlow.id ? 'Flow átnevezve' : 'Flow elnevezve',
        message: `A flow neve: "${editingFlowName.trim()}"`
      });
    } catch (error) {
      console.error('Error renaming flow:', error);
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Átnevezési hiba',
        message: `Hiba történt a flow átnevezése során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
      });
    } finally {
      setIsSaving(false);
    }
  }, [currentFlow, user, editingFlowName]);

  // Flow átnevezés megszakítása
  const cancelEditingFlowName = useCallback(() => {
    setIsEditingFlowName(false);
    setEditingFlowName('');
  }, []);

  // Flow törlése - Modal megnyitása
  const onDeleteFlow = useCallback(() => {
    if (!currentFlow?.id || !currentFlow?.name) {
      return;
    }
    setFlowToDelete({ id: currentFlow.id, name: currentFlow.name });
  }, [currentFlow]);

  // Template kiválasztása
  const onSelectTemplate = useCallback((template: FlowTemplate) => {
    setCurrentFlow({
      ...template,
      id: '',
      organizationId: user?.organizationId || '',
      createdBy: user?.uid || '',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    setNodes(template.nodes || []);
    setEdges(template.edges || []);
    setShowTemplates(false);
    
    console.log('Template selected:', template.name);
  }, [user, setNodes, setEdges]);

  // Kiválasztott node adatai
  const selectedNodeData = useMemo(() => {
    return selectedNode ? nodes.find(n => n.id === selectedNode) : null;
  }, [selectedNode, nodes]);

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
  {/* Header - CommonPageHeader a gombokkal */}
  <CommonPageHeader title="Flow Builder" subtitle="Visual Automation Builder" icon={CogIcon}>
    {/* Státusz Kártya */}
    <div className="h-10 flex items-center gap-2 px-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
      {/* Engine status */}
      {engineStatus && (
        <div 
          className="flex items-center gap-1.5" 
          title={`Engine: ${engineStatus.isOnline ? 'Online' : 'Offline'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
          </svg>
          <div className={`w-2 h-2 rounded-full ${engineStatus.isOnline ? 'bg-green-400' : 'bg-red-400'}`}></div>
        </div>
      )}

      {/* Separator */}
      <div className="h-4 w-px bg-white/20"></div>

      {/* Scheduler status */}
      {schedulerStatus && (
        <div 
          className="flex items-center gap-1.5" 
          title={`Scheduler: ${schedulerStatus.is_running ? 'Aktív' : 'Inaktív'}`}
        >
          <ClockIcon className="w-4 h-4 text-white/90" />
          <div className={`w-2 h-2 rounded-full ${schedulerStatus.is_running ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}></div>
        </div>
      )}

      {/* Separator */}
      <div className="h-4 w-px bg-white/20"></div>

      {/* Validation status */}
      {validationResult && (
        <div 
          className="flex items-center gap-1.5"
          title={validationResult.isValid ? 'Flow érvényes' : 'Flow érvénytelen'}
        >
          {validationResult.isValid ? (
            <CheckIcon className="h-4 w-4 text-green-400" />
          ) : (
            <ExclamationTriangleIcon className="h-4 w-4 text-red-400" />
          )}
        </div>
      )}
    </div>

    {/* Separator */}
    <div className="h-6 w-px bg-white/30"></div>

    {/* Flow selector és név szerkesztés */}
    <div className="flex items-center gap-2">
      {!isEditingFlowName ? (
        <>
          {/* Autocomplete Flow Selector */}
          <div className="relative min-w-[320px]">
            <input
              type="text"
              value={flowSearchTerm || currentFlow?.name || ''}
              onChange={(e) => {
                setFlowSearchTerm(e.target.value);
                setShowFlowDropdown(true);
              }}
              onFocus={() => setShowFlowDropdown(true)}
              placeholder="🔍 Flow keresése vagy új létrehozása..."
              className="h-10 w-full pl-4 pr-10 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-200 shadow-lg"
              disabled={isLoading}
            />
            
            {/* Dropdown arrow */}
            <button
              onClick={() => setShowFlowDropdown(!showFlowDropdown)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown lista */}
            {showFlowDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowFlowDropdown(false)}></div>
                <div className="absolute top-full mt-2 left-0 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-20 max-h-96 overflow-y-auto">
                  {/* Új Flow opció */}
                  <button
                    onClick={() => {
                      onNewFlow();
                      setFlowSearchTerm('');
                      setShowFlowDropdown(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 border-b border-gray-200 dark:border-gray-700"
                  >
                    <span className="text-xl">➕</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Új Flow létrehozása</span>
                  </button>

                  {/* Flow lista */}
                  {availableFlows
                    .filter(flow => 
                      flowSearchTerm === '' || 
                      flow.name.toLowerCase().includes(flowSearchTerm.toLowerCase())
                    )
                    .sort((a, b) => {
                      const dateA = new Date(a.updatedAt || a.createdAt || 0);
                      const dateB = new Date(b.updatedAt || b.createdAt || 0);
                      return dateB.getTime() - dateA.getTime();
                    })
                    .map(flow => {
                      const hasScheduleTrigger = flow.nodes?.some(node => node.type === 'schedule_trigger');
                      const isActive = currentFlow?.id === flow.id;
                      
                      return (
                        <button
                          key={flow.id}
                          onClick={() => {
                            onLoadFlow(flow.id);
                            setFlowSearchTerm('');
                            setShowFlowDropdown(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 ${
                            isActive ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <span className="text-xl">{hasScheduleTrigger ? '⏰' : '📄'}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {flow.name}
                            </div>
                            {flow.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {flow.description}
                              </div>
                            )}
                          </div>
                          {isActive && (
                            <CheckIcon className="h-4 w-4 text-blue-500" />
                          )}
                        </button>
                      );
                    })}

                  {/* Nincs találat */}
                  {flowSearchTerm && availableFlows.filter(flow => 
                    flow.name.toLowerCase().includes(flowSearchTerm.toLowerCase())
                  ).length === 0 && (
                    <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      <p className="text-sm">Nincs találat: "{flowSearchTerm}"</p>
                      <button
                        onClick={() => {
                          onNewFlow();
                          setFlowSearchTerm('');
                          setShowFlowDropdown(false);
                        }}
                        className="mt-2 text-sm text-blue-500 hover:text-blue-600"
                      >
                        Új flow létrehozása ezzel a névvel
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
              
              {currentFlow && (
                <>
                  <button
                    onClick={startEditingFlowName}
                    className="h-10 w-10 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30 transition-all duration-200 shadow-lg flex items-center justify-center"
                    title={currentFlow.id ? "Flow átnevezése" : "Flow elnevezése"}
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                  
                  {currentFlow.id && (
                    <button
                      onClick={onDeleteFlow}
                      disabled={isLoading}
                      className="h-10 w-10 rounded-lg bg-red-500/20 backdrop-blur-md border border-red-400/30 text-white hover:bg-red-500/30 hover:border-red-400/40 transition-all duration-200 shadow-lg flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Flow törlése"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editingFlowName}
                onChange={(e) => setEditingFlowName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    saveFlowName();
                  } else if (e.key === 'Escape') {
                    cancelEditingFlowName();
                  }
                }}
                className="h-10 px-4 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-all duration-200 shadow-lg placeholder-white/50 min-w-[280px]"
                placeholder="Flow neve..."
                autoFocus
              />
              <button
                onClick={saveFlowName}
                disabled={!editingFlowName.trim() || isSaving}
                className="h-10 w-10 rounded-lg bg-green-500/30 backdrop-blur-md border border-green-400/40 text-white hover:bg-green-500/40 hover:border-green-400/60 transition-all duration-200 shadow-lg flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                title="Mentés"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
              <button
                onClick={cancelEditingFlowName}
                className="h-10 w-10 rounded-lg bg-red-500/30 backdrop-blur-md border border-red-400/40 text-white hover:bg-red-500/40 hover:border-red-400/60 transition-all duration-200 shadow-lg flex items-center justify-center"
                title="Mégse"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* Flow Manager gomb */}
        {schedulerStatus && (
          <button
            onClick={() => setShowScheduledFlows(true)}
            className={`h-10 flex items-center gap-2 px-3 rounded-lg backdrop-blur-md border transition-all duration-200 shadow-lg ${
              schedulerStatus.scheduled_flows_count > 0
                ? 'bg-blue-500/30 border-blue-400/50 hover:bg-blue-500/50'
                : 'bg-white/10 border-white/20 hover:bg-white/20'
            }`}
            title="Flow-k kezelése"
          >
            <span className="text-xl">🎛️</span>
            <span className="text-sm text-white font-medium">
              Flow Manager {schedulerStatus.scheduled_flows_count > 0 ? `(${schedulerStatus.scheduled_flows_count})` : ''}
            </span>
          </button>
        )}

        {/* Separator */}
        <div className="h-6 w-px bg-white/30"></div>

        {/* Műveletek Dropdown (Egyesített) */}
        <div className="relative">
          <button
            onClick={() => setShowActionsMenu(!showActionsMenu)}
            className="h-10 flex items-center gap-2 px-3 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30 transition-all duration-200 shadow-lg"
          >
            <RectangleStackIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Műveletek</span>
            <ChevronDownIcon className="h-4 w-4" />
          </button>

          {showActionsMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActionsMenu(false)}></div>
              <div className="absolute top-full mt-2 right-0 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                {/* Flow műveletek */}
                <button
                  onClick={() => { onNewFlow(); setShowActionsMenu(false); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200"
                >
                  <span className="text-xl">➕</span>
                  <span className="text-sm font-medium">Új Flow</span>
                </button>
                <button
                  onClick={() => { setShowTemplates(true); setShowActionsMenu(false); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200"
                >
                  <DocumentDuplicateIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Sablonok</span>
                </button>
                <button
                  onClick={() => { setShowExecutionHistory(true); setShowActionsMenu(false); }}
                  disabled={!currentFlow?.id}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ClockIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Előzmények</span>
                </button>
                
                <div className="border-t border-gray-200 dark:border-gray-700"></div>
                
                {/* Import/Export */}
                <button
                  onClick={() => { onExportFlow(); setShowActionsMenu(false); }}
                  disabled={!currentFlow}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentArrowDownIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Export</span>
                </button>
                <label className="w-full px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200 cursor-pointer">
                  <DocumentArrowUpIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Import</span>
                  <input type="file" accept=".json" onChange={(e) => { onImportFlow(e); setShowActionsMenu(false); }} className="hidden" />
                </label>
                
                <div className="border-t border-gray-200 dark:border-gray-700"></div>
                
                {/* Eszközök */}
                <button
                  onClick={() => { setShowCredentials(true); setShowActionsMenu(false); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200"
                >
                  <KeyIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">API Kulcsok</span>
                </button>
                <button
                  onClick={() => { setShowLogs(true); setShowActionsMenu(false); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Execution Logok</span>
                </button>
                <button
                  onClick={() => { onExecuteFlow(true); setShowActionsMenu(false); }}
                  disabled={!currentFlow?.id || isExecuting || !validationResult?.isValid || !engineStatus?.isOnline}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <EyeIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Monitor</span>
                </button>
                
                <div className="border-t border-gray-200 dark:border-gray-700"></div>
                
                {/* Dokumentáció */}
                <button
                  onClick={() => { setShowDocumentation(true); setShowActionsMenu(false); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-gray-700 dark:text-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium">Dokumentáció</span>
                </button>
              </div>
            </>
          )}
        </div>

        {/* Separator */}
        <div className="h-6 w-px bg-white/30"></div>

        {/* Mentés gomb (mindig látható) */}
        <button
          onClick={onSaveFlow}
          disabled={isSaving}
          className="h-10 flex items-center gap-2 px-4 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-md border border-green-400/30 text-white hover:from-green-500/30 hover:to-emerald-500/30 hover:border-green-400/40 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span className="text-sm font-medium">{isSaving ? 'Mentés...' : 'Mentés'}</span>
        </button>
        
        {/* Separator */}
        <div className="h-6 w-px bg-white/30"></div>

        {/* Execute Buttons Group */}
        <div className="flex items-center gap-2">
          {/* Simple Execute Button */}
          <button
            onClick={() => onExecuteFlow(false)}
            disabled={isExecuting || !currentFlow?.id || !validationResult?.isValid || !engineStatus?.isOnline}
            className="h-10 flex items-center gap-2 px-4 rounded-lg bg-gradient-to-r from-green-500/30 to-emerald-500/30 backdrop-blur-md border-2 border-green-400/40 text-white hover:from-green-500/40 hover:to-emerald-500/40 hover:border-green-400/60 transition-all duration-200 shadow-lg hover:shadow-green-500/50 disabled:opacity-40 disabled:cursor-not-allowed font-semibold"
            title="Flow futtatása háttérben - gyors végrehajtás értesítéssel"
          >
            {isExecuting ? (
              <>
                <PauseIcon className="w-5 h-5 animate-pulse" />
                <span className="text-sm">Futtatás...</span>
              </>
            ) : (
              <>
                <PlayIcon className="w-5 h-5" />
                <span className="text-sm">Futtatás</span>
              </>
            )}
          </button>

        </div>
      </CommonPageHeader>


      {/* Main Content - Teljes magasság */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Node paletta - Responsív */}
        <div className={`
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:relative fixed inset-y-0 left-0 z-40
          w-72 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full
          transition-transform duration-300 ease-in-out
        `}>
          <FlowSidebar onAddNode={onAddNode} />
        </div>

        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={toggleSidebar}
          />
        )}

        {/* Canvas */}
        <div className="flex-1 relative pb-24">
          {/* Mobile Sidebar Indicator - Vastagabb lila csík bal oldalon */}
          <div className={`
            md:hidden absolute left-0 top-1/2 -translate-y-1/2 z-[9998]
            w-3 h-24 bg-gradient-to-b from-purple-500 to-blue-500 rounded-r-lg shadow-lg
            transition-all duration-300 cursor-pointer
            ${isSidebarOpen ? 'opacity-0 -translate-x-4' : 'opacity-80 hover:opacity-100 hover:w-4 hover:shadow-xl'}
          `}
            onClick={toggleSidebar}
            title="Node paletta megnyitása"
          />

          <ReactFlowProvider>
            <FlowCanvas
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onDeleteNode={onDeleteNode}
              onInit={setReactFlowInstance}
              isExecuting={isExecuting}
              executionProgress={executionProgress}
            />
          </ReactFlowProvider>
        </div>

        {/* Properties Panel - Desktop: sidebar, Mobile: modal */}
        {selectedNodeData && (
          <>
            {/* Desktop Properties Panel */}
            <div className="hidden md:block w-[360px] flex-shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 h-full pb-24">
              <NodePropertiesPanel
                node={selectedNodeData as FlowNode}
                onUpdateNode={onUpdateNode}
                onDeleteNode={onDeleteNode}
                onClose={() => setSelectedNode(null)}
              />
            </div>

            {/* Mobile Properties Modal */}
            <div 
              className="md:hidden fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-end"
              onClick={() => setSelectedNode(null)}
            >
              <div 
                className="w-full bg-white dark:bg-gray-800 rounded-t-xl max-h-[80vh] overflow-hidden animate-slide-up"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Mobile Modal Handle */}
                <div className="flex justify-center py-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                </div>
                
                <NodePropertiesPanel
                  node={selectedNodeData as FlowNode}
                  onUpdateNode={onUpdateNode}
                  onDeleteNode={onDeleteNode}
                  onClose={() => setSelectedNode(null)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Flow Templates Modal */}
      <Modal
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        title="Flow Sablonok"
        size="4xl"
        zIndex={60}
      >
        <FlowTemplates 
          onSelectTemplate={onSelectTemplate}
          onClose={() => setShowTemplates(false)}
        />
      </Modal>

      {/* Credentials Manager Modal */}
      {showCredentials && (
        <Modal
          isOpen={showCredentials}
          onClose={() => setShowCredentials(false)}
          title="🔐 API Kulcsok és Jelszavak"
          size="7xl"
        >
          <div className="p-6">
            <CredentialsManager />
          </div>
        </Modal>
      )}

      {/* Scheduled Flows Modal */}
      {showScheduledFlows && (
        <Modal
          isOpen={showScheduledFlows}
          onClose={() => setShowScheduledFlows(false)}
          title="🎛️ Flow-k Kezelése"
          size="6xl"
        >
          <div className="p-6">
            {/* Header Info */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <span className="text-2xl">📊</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Összes Flow</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{availableFlows.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <span className="text-2xl">✅</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Aktív Flow-k</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {availableFlows.filter(f => f.isActive !== false).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <span className="text-2xl">⏰</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Ütemezett</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {availableFlows.filter(f => f.nodes?.some(n => n.type === 'schedule_trigger')).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <span className="text-2xl">🔧</span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Scheduler</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {schedulerStatus?.is_running ? '🟢 Aktív' : '🔴 Inaktív'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {availableFlows
                .sort((a, b) => {
                  // Ütemezett flow-k felül
                  const aScheduled = a.nodes?.some(node => node.type === 'schedule_trigger');
                  const bScheduled = b.nodes?.some(node => node.type === 'schedule_trigger');
                  if (aScheduled && !bScheduled) return -1;
                  if (!aScheduled && bScheduled) return 1;
                  return 0;
                })
                .map(flow => {
                  const scheduleTrigger = flow.nodes?.find(node => node.type === 'schedule_trigger');
                  const hasSchedule = !!scheduleTrigger;
                  const cronExpression = scheduleTrigger?.data?.config?.cron || '';
                  const isScheduleActive = scheduleTrigger?.data?.config?.is_active !== false;
                  const isFlowActive = flow.isActive !== false;
                  
                  const handleToggleSchedule = async (e: React.MouseEvent) => {
                    e.stopPropagation();
                    
                    if (!flow.id || !user?.organizationId || !user?.uid) return;
                    
                    try {
                      // Frissítjük a schedule trigger is_active értékét
                      const updatedNodes = flow.nodes?.map(node => {
                        if (node.type === 'schedule_trigger') {
                          return {
                            ...node,
                            data: {
                              ...node.data,
                              config: {
                                ...node.data.config,
                                is_active: !isScheduleActive
                              }
                            }
                          };
                        }
                        return node;
                      });
                      
                      await updateFlow(
                        flow.id,
                        { nodes: updatedNodes },
                        user.organizationId,
                        user.uid
                      );
                      
                      // Flow lista frissítése
                      const updatedFlows = await listFlows(user.organizationId);
                      setAvailableFlows(updatedFlows);
                      
                      // Scheduler státusz frissítése
                      await fetchSchedulerStatus();
                      
                      setNotification({
                        isOpen: true,
                        type: 'success',
                        title: isScheduleActive ? 'Ütemezés kikapcsolva' : 'Ütemezés bekapcsolva',
                        message: `"${flow.name}" ${isScheduleActive ? 'nem fog' : 'fog'} automatikusan futni`
                      });
                    } catch (error) {
                      console.error('Error toggling schedule:', error);
                      setNotification({
                        isOpen: true,
                        type: 'error',
                        title: 'Hiba',
                        message: 'Nem sikerült módosítani az ütemezést'
                      });
                    }
                  };
                  
                  return (
                    <div
                      key={flow.id}
                      className={`group relative bg-gradient-to-r ${
                        isFlowActive 
                          ? 'from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/10' 
                          : 'from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-700/50'
                      } border-2 ${
                        isFlowActive
                          ? 'border-blue-200 dark:border-blue-800'
                          : 'border-gray-200 dark:border-gray-700'
                      } rounded-xl p-5 hover:shadow-lg transition-all duration-200`}
                    >
                      {/* Status Indicator */}
                      <div className={`absolute top-0 left-0 w-1 h-full rounded-l-xl ${
                        isFlowActive ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>

                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                          hasSchedule
                            ? 'bg-blue-100 dark:bg-blue-900/30'
                            : 'bg-gray-100 dark:bg-gray-700'
                        }`}>
                          <span className="text-2xl">
                            {hasSchedule ? '⏰' : '⚡'}
                          </span>
                        </div>

                        {/* Content */}
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            onLoadFlow(flow.id);
                            // Ne zárjuk be a popup-ot, hogy tovább tudjunk kezelni flow-kat
                          }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                              {flow.name}
                            </h3>
                            {hasSchedule && (
                              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                                Ütemezett
                              </span>
                            )}
                          </div>
                          
                          {hasSchedule && cronExpression && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">Cron:</span>
                              <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs font-mono text-gray-700 dark:text-gray-300">
                                {cronExpression}
                              </code>
                            </div>
                          )}
                          
                          {flow.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                              {flow.description}
                            </p>
                          )}

                          {/* Metadata */}
                          <div className="grid grid-cols-2 gap-2 mt-3">
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 dark:text-gray-400">📦 Nodes:</span>
                              <span className="font-semibold text-gray-700 dark:text-gray-300">{flow.nodes?.length || 0}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 dark:text-gray-400">🔗 Edges:</span>
                              <span className="font-semibold text-gray-700 dark:text-gray-300">{flow.edges?.length || 0}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 dark:text-gray-400">👤 Létrehozta:</span>
                              <span className="font-semibold text-gray-700 dark:text-gray-300 truncate">
                                {flow.createdBy === user?.uid ? 'Te' : (user?.displayName || user?.email?.split('@')[0] || 'Felhasználó')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500 dark:text-gray-400">📅 Frissítve:</span>
                              <span className="font-semibold text-gray-700 dark:text-gray-300">
                                {flow.updatedAt ? new Date(flow.updatedAt).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }) : 'N/A'}
                              </span>
                            </div>
                            {flow.category && (
                              <div className="flex items-center gap-2 text-xs col-span-2">
                                <span className="text-gray-500 dark:text-gray-400">🏷️ Kategória:</span>
                                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-medium">
                                  {flow.category}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex-shrink-0 flex flex-col items-end gap-3">
                          {/* Toggle kapcsoló */}
                          {hasSchedule && (
                            <button
                              onClick={handleToggleSchedule}
                              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-200 ${
                                isScheduleActive 
                                  ? 'bg-green-500 shadow-lg shadow-green-500/50' 
                                  : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                              title={isScheduleActive ? 'Ütemezés kikapcsolása' : 'Ütemezés bekapcsolása'}
                            >
                              <span
                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                  isScheduleActive ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              isFlowActive
                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {isFlowActive ? '● Aktív' : '○ Inaktív'}
                            </span>
                            
                            {/* Törlés gomb */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setFlowToDelete({ id: flow.id, name: flow.name });
                              }}
                              className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                              title="Flow törlése"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              
              {availableFlows.filter(flow => flow.nodes?.some(node => node.type === 'schedule_trigger')).length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>Nincs ütemezett flow</p>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Execution Monitor Modal */}
      {showExecutionMonitor && currentExecutionId && (
        <ExecutionMonitor
          executionId={currentExecutionId}
          flowNodes={nodes.map(n => ({
            id: n.id,
            type: n.type || 'unknown',
            data: { label: n.data.label || 'Unknown' }
          }))}
          isVisible={showExecutionMonitor}
          onClose={() => {
            setShowExecutionMonitor(false);
            setCurrentExecutionId(null);
          }}
        />
      )}

      {/* Execution History Modal */}
      {showExecutionHistory && currentFlow?.id && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl h-[80vh] max-h-[600px]">
            <FlowExecutionHistory 
              flowId={currentFlow.id}
              onClose={() => setShowExecutionHistory(false)}
            />
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={() => setNotification({ isOpen: false, type: 'info', title: '', message: '' })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        confirmText="OK"
      />

      {/* Execution Logs Modal */}
      {showLogs && (
        <Modal
          isOpen={showLogs}
          onClose={() => setShowLogs(false)}
          title="📊 Execution Logok"
          size="4xl"
        >
          <div className="p-6">
            <ExecutionLogs organizationId={user?.organizationId || ''} />
          </div>
        </Modal>
      )}

      {/* Flow Builder Documentation */}
      <FlowBuilderDocumentation
        isOpen={showDocumentation}
        onClose={() => setShowDocumentation(false)}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={!!flowToDelete}
        onClose={() => setFlowToDelete(null)}
        onConfirm={async () => {
          if (!flowToDelete || !user?.organizationId) return;
          
          try {
            await deleteFlow(flowToDelete.id, user.organizationId);
            
            // Flow lista frissítése
            const updatedFlows = await listFlows(user.organizationId);
            setAvailableFlows(updatedFlows);
            
            // Scheduler státusz frissítése
            await fetchSchedulerStatus();
            
            // Ha a törölt flow volt betöltve, új flow-ra váltunk
            if (currentFlow?.id === flowToDelete.id) {
              onNewFlow();
            }
            
            setNotification({
              isOpen: true,
              type: 'success',
              title: 'Flow törölve',
              message: `"${flowToDelete.name}" sikeresen törölve`
            });
            
            setFlowToDelete(null);
          } catch (error) {
            console.error('Error deleting flow:', error);
            setNotification({
              isOpen: true,
              type: 'error',
              title: 'Törlési hiba',
              message: 'Nem sikerült törölni a flow-t'
            });
            setFlowToDelete(null);
          }
        }}
        title="Flow törlése"
        deleteMessage={`Biztosan törölni szeretnéd ezt a flow-t?`}
        deleteWarning={`"${flowToDelete?.name}" - Ez a művelet nem visszavonható!`}
        danger={true}
        zIndex={70}
      />
    </div>
  );
};

// Helper függvények
function getNodeLabel(nodeType: FlowNodeType): string {
const labels: Record<FlowNodeType, string> = {
    // Trigger típusok
    trigger: 'Trigger',
    webhook_trigger: 'Webhook',
    schedule_trigger: 'Ütemezett',
    form_trigger: 'Űrlap beküldés',
    email_trigger: 'Email érkezés',
    file_trigger: 'Fájl feltöltés',
    telegram_trigger: 'Telegram Üzenet',
    database_trigger: 'Adatbázis változás',
    manual_trigger: 'Manuális indítás',
    // Action típusok
    action: 'Művelet',
    condition: 'Feltétel',
    switch: 'Elágazás',
    merge: 'Egyesítés',
    wait: 'Várakozás',
    loop: 'Iteráció',
    form: 'Űrlap',
    approval: 'Jóváhagyás',
    notification: 'Email értesítés küldése',
    smtp_email: 'Email küldés SMTP-vel',
    telegram_message: 'Telegram üzenet küldése',
    sms_send: 'SMS üzenet küldése',
    flintsign_sign: 'Dokumentum aláírása FlintSign-nal',
    data_transform: 'Adatátalakítás',
    integration: 'Integráció',
    crm_action: 'CRM Művelet',
    // Új node típusok
    debug_logger: 'Debug napló',
    variable_set: 'Változó beállítás',
    variable_get: 'Változó lekérdezés',
    text_processor: 'Szöveg feldolgozó',
    math_operation: 'Matematikai művelet',
    date_time: 'Dátum/idő',
    random_generator: 'Véletlen generátor',
    counter: 'Számláló',
    multi_condition: 'Összetett feltétel',
    data_filter: 'Adatok szűrése',
    data_sort: 'Adatok rendezése',
    json_processor: 'JSON feldolgozó',
    string_formatter: 'Szöveg formázó',
    list_processor: 'Lista feldolgozó',
    // Fájl műveletek
    file_read: 'Fájl olvasás',
    file_write: 'Fájl írás',
    pdf_generator: 'PDF Generálás',
    excel_processor: 'Excel feldolgozó',
    image_processor: 'Kép feldolgozó',
    file_converter: 'Fájl konvertáló',
    // Data Parsers
    csv_parser: 'CSV Feldolgozó',
    xml_parser: 'XML Feldolgozó',
    yaml_parser: 'YAML Feldolgozó',
    base64_coder: 'Base64 Kódoló',
    data_validator: 'Adat Validátor',
    // Database műveletek
    database_query: 'Database Query',
    database_insert: 'Database Insert',
    database_update: 'Database Update',
    database_delete: 'Database Delete',
    // Error Handling
    try_catch: 'Try-Catch',
    retry: 'Retry',
    error_logger: 'Error Logger',
    timeout: 'Timeout',
    // AI Integration címkék
    openai_chat: 'OpenAI ChatGPT',
    claude_chat: 'Anthropic Claude',
    deepseek_chat: 'DeepSeek AI',
    gemini_chat: 'Google Gemini',
    ai_text_generator: 'AI Szöveg Generátor',
    ai_image_generator: 'AI Kép Generátor',
    ai_text_analyzer: 'AI Szöveg Elemző',
    ai_translator: 'AI Fordító',
    ai_summarizer: 'AI Összefoglaló',
  };
  
  return labels[nodeType] || nodeType;
}

function getDefaultInputs(nodeType: FlowNodeType) {
  // TODO: Node típus alapján alapértelmezett inputok
  return [];
}

function getDefaultOutputs(nodeType: FlowNodeType) {
  // TODO: Node típus alapján alapértelmezett outputok
  return [];
}

export default FlowBuilder;
